/**
 * MODAL
 * Dialog
 */
import { useEffect, useRef, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import Space from "./space"
import Button from "./button"
import BubbleText from "./BubbleText"
import { STATUS_LOADED, STATUS_SAVING, STATUS_SEARCHING } from "@/redux/_status.types"
import SearchBar from "./search-bar"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"
import isTablet from "@/utils/is-tablet.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faChevronLeft } from "@fortawesome/free-solid-svg-icons"
import Tooltip from "./tooltip"

interface OwnProps {
  backgroundColor?: string
  children?: any
  disableKeyEvents?: boolean
  disableClickOutside?: boolean
  disableOverflow?: boolean
  isCloseButtonVisible?: boolean
  isTabletFullScreenDisabled?: boolean
  isLarge?: boolean
  isFullScreen?: boolean
  isFullscreen?: boolean
  onCancel?: Function
  onClose?: Function
  onDelete?: Function
  onSearch?: Function
  onNext?: Function
  onNextCta?: string
  status?: string
  title?: React.ReactNode
  nextDisabled?: boolean
  nextDisabledTooltip?: string
  nextDisabledTooltipPosition?: "top" | "bottom" | "left" | "right"
  className?: string
  contentClassName?: string
  isAnimated?: boolean //Animation d'ouverture et de fermeture (opt-in : la fermeture est alors retardée de l'animation)
  contentHeight?: number | string
  displayOwl?: boolean
  displayOwlTop?: number
  footer?: React.ReactNode
  footerStart?: React.ReactNode
  zIndex?: number
  //lockReason : etape inaccessible, avec le motif affiche au survol
  wizardSteps?: Array<{ id: string; label: string; description?: string; lockReason?: string }>
  wizardOwlText?: string
  wizardSidebarFooter?: React.ReactNode
  isWizardBackVisible?: boolean
  onWizardBack?: () => void
  activeWizardStepId?: string
  //Vue d'ensemble : elle n'est pas une etape, toutes les etapes s'affichent donc terminees
  isWizardOverview?: boolean
  onWizardStepClick?: (id: string) => void
  footerLeft?: React.ReactNode //Left of the buttons of the footer
}

type Props = OwnProps & WithTranslation

function Modal(props: Props) {
  const { t } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const wizardContentRef = useRef<HTMLDivElement>(null)

  //Nouvelle etape du wizard : on repart du haut du contenu
  useEffect(() => {
    wizardContentRef.current?.scrollTo(0, 0)
  }, [props.activeWizardStepId, props.isWizardOverview])

  //Searching
  const [isSearching, setIsSearching] = useState(false)

  //Déclenché par close() ; le vrai démontage (props.onClose) n'arrive qu'une fois
  //l'animation de sortie terminée (cf onExitComplete plus bas) — permet une anim de
  //fermeture sans rien changer aux ~50 endroits qui font juste {isOpen && <Modal/>}
  //Pas d'animation pour le wizard, qui n'est pas dans l'AnimatePresence
  const [isClosing, setIsClosing] = useState(false)
  const isAnimationsDisabled = useAnimationsDisabled()
  const isFading = Boolean(props.isAnimated) && !props.wizardSteps && !isAnimationsDisabled

  //Detect is tablet
  const isT = isTablet()
  const isFullScreen = (isT && !props.isTabletFullScreenDisabled) || props.isFullScreen || props.isFullscreen

  //Detect action
  //> Enter => go next
  //> Escape => close modal
  useEffect(() => {
    if (!props.disableKeyEvents) {
      const onKeyDown = (e) => {
        //Une modale enfant (risque, photo...) est seule a traiter le clavier.
        const containers = document.querySelectorAll(".modal-container")
        if (containers[containers.length - 1] !== containerRef.current) return
        if (e.key === "Enter") {
          next()
        } else if (e.key === "Escape") {
          close()
        }
      }

      window.addEventListener("keydown", onKeyDown)
      return () => {
        window.removeEventListener("keydown", onKeyDown)
      }
    }
  })

  //Close dialog
  //Close dialog — avec l'animation, déclenche l'anim de sortie et le vrai onClose arrive via onExitComplete ;
  //sans animation, ferme tout de suite comme avant
  function close() {
    if (isFading) {
      setIsClosing(true)
    } else {
      handleExitComplete()
    }
  }

  //Appelé une fois l'anim de sortie terminée et l'élément réellement retiré du DOM
  function handleExitComplete() {
    if (props.onClose) {
      props.onClose()
    }
  }

  //Close dialog
  function cancel() {
    if (props.onCancel) {
      props.onCancel()
    }
  }

  //Clic outside trigger close
  function clickOutside() {
    if (!props.disableClickOutside) {
      close()
    }
  }

  //Click on delete
  function deleteItem() {
    if (props.onDelete) {
      props.onDelete()
    }
  }

  //Custom style
  function getStyle() {
    const style: any = {
      backgroundColor: props.backgroundColor,
      minWidth: isFullScreen ? undefined : props.isLarge ? "780px" : undefined,
      maxWidth: isFullScreen ? undefined : props.isLarge ? "780px" : undefined
    }

    if (props.displayOwl) {
      style.backgroundImage = `url(${require("@/assets/owl.png")})`
      style.backgroundRepeat = "no-repeat"
      style.backgroundPosition = "-64px " + (props.displayOwlTop ? props.displayOwlTop : "8") + "px"
      style.backgroundSize = "260px auto"
      style.borderRadius = "12px"
      style.overflow = "hidden"
    }

    return style
  }

  //Function next
  function next() {
    if (props.onNext) {
      props.onNext()
    }
  }

  //Search
  function search(value) {
    setIsSearching(false)
    if (props.onSearch) props.onSearch(value)
  }

  if (props.wizardSteps) {
    //En vue d'ensemble l'index depasse la derniere etape : toutes sont cochees, aucune n'est active,
    //et le retour interne cible naturellement la derniere etape
    const activeStepIndex = props.isWizardOverview
      ? props.wizardSteps.length
      : Math.max(
          0,
          props.wizardSteps.findIndex((step) => step.id === props.activeWizardStepId)
        )
    const previousWizardStep = activeStepIndex > 0 ? props.wizardSteps[activeStepIndex - 1] : null

    return createPortal(
      <div ref={containerRef} className="modal-container flex" style={{ zIndex: props.zIndex }} onClick={() => clickOutside()}>
        <div
          className={`modal-fullscreen overflow-hidden p-0 ${props.className || ""}`}
          onClick={(e) => {
            e.stopPropagation()
          }}
          style={{
            ...getStyle(),
            height: "86vh",
            maxHeight: "86vh",
            maxWidth: "94vw",
            overflow: "hidden",
            padding: 0,
            width: "94vw"
          }}>
          <div className="flex min-h-0 flex-1">
            {/* Defile quand les etapes depassent la hauteur ; les infobulles des etapes passent par un portail */}
            <aside className="flex w-[230px] shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-[#f6f6f6] p-[30px]">
              {props.title && <h1 className="mt-0 leading-tight text-slate-900">{props.title}</h1>}
              <div className="mb-40 font-semibold text-idt-primary-main">
                {props.isWizardOverview ? (
                  "Vue d'ensemble"
                ) : (
                  <>
                    Étape {activeStepIndex + 1} sur {props.wizardSteps.length}
                  </>
                )}
              </div>

              <div className="flex flex-col">
                {props.wizardSteps.map((step, index) => {
                  const isActive = !props.isWizardOverview && step.id === props.activeWizardStepId
                  const isDone = index < activeStepIndex
                  const isLast = index === props.wizardSteps!.length - 1
                  const isLocked = Boolean(step.lockReason)

                  const stepButton = (
                    <button
                      type="button"
                      className={`group flex w-full overflow-visible border-0 bg-transparent p-0 text-left ${
                        isLocked ? "cursor-default opacity-50" : "_hover"
                      }`}
                      onClick={isLocked ? undefined : () => props.onWizardStepClick?.(step.id)}>
                      <div className="mr-10 flex w-[42px] shrink-0 flex-col items-center overflow-visible">
                        <span
                          className={`flex items-center justify-center rounded-full text-sm font-bold ${
                            isActive ? "bg-idt-primary-main text-white shadow-custom" : "bg-transparent"
                          }`}
                          style={{
                            border: `2px solid ${isActive ? "#00c2e0" : "#CBD5E1"}`,
                            boxSizing: "border-box",
                            color: isActive ? "white" : "#94A3B8",
                            height: 34,
                            width: 34
                          }}>
                          {isDone ? <FontAwesomeIcon className="text-idt-primary-main" icon={faCheck} /> : index + 1}
                        </span>
                        {!isLast && <span className="h-[28px] w-[2px] bg-slate-200" />}
                      </div>

                      <span className="flex h-[34px] min-w-0 items-center">
                        <span
                          className={`block text-base font-bold leading-tight ${
                            isActive ? "text-slate-900" : "grey-t"
                          }`}>
                          {step.label}
                        </span>
                      </span>
                    </button>
                  )

                  //usePortal : la modale est en overflow hidden, l'infobulle serait rognee
                  return isLocked ? (
                    <Tooltip key={step.id} className="w-full" position="right" text={step.lockReason} usePortal>
                      {stepButton}
                    </Tooltip>
                  ) : (
                    <div key={step.id}>{stepButton}</div>
                  )
                })}
              </div>

              {props.wizardSidebarFooter && <div className="mt-auto pt-20">{props.wizardSidebarFooter}</div>}
            </aside>

            <section className="relative flex min-w-0 flex-1 flex-col">
              {props.onClose && (
                <button
                  type="button"
                  className="_hover absolute right-[30px] top-20 z-10 border-0 bg-transparent p-0 text-[38px] leading-none text-slate-500"
                  onClick={close}>
                  ×
                </button>
              )}

              <div ref={wizardContentRef} className="min-h-0 flex-1 overflow-y-auto px-40 py-[30px]">
                <div className="flex min-h-full flex-col">
                  {props.wizardOwlText && (
                    <div className="relative mb-20 flex min-h-[74px] items-start">
                      <img
                        src={require("@/assets/owl.png")}
                        alt="owl"
                        className="absolute h-auto w-[100px]"
                        style={{ left: -82, top: -24, transform: "rotate(35deg)" }}
                      />
                      <div className="ml-40 mt-20 w-[min(760px,calc(100%-80px))]">
                        <BubbleText orientation="left">
                          <span className="block text-[18px] font-semibold leading-tight text-slate-900">
                            {props.wizardOwlText}
                          </span>
                        </BubbleText>
                      </div>
                    </div>
                  )}

                  {props.children}
                </div>
              </div>

              <div className="flex shrink-0 border-t border-slate-100 bg-white px-[30px] py-20">
                {props.isWizardBackVisible !== false && props.onWizardBack && (
                  <Button icon={faChevronLeft} onClick={props.onWizardBack}>
                    Retour
                  </Button>
                )}

                {props.isWizardBackVisible !== false &&
                  !props.onWizardBack &&
                  previousWizardStep &&
                  props.onWizardStepClick && (
                    <Button icon={faChevronLeft} onClick={() => props.onWizardStepClick?.(previousWizardStep.id)}>
                      Retour
                    </Button>
                  )}

                {props.onDelete && (
                  <Button isWithBorder onClick={deleteItem}>
                    {t("utils_delete")}
                  </Button>
                )}

                {props.footerStart}

                <Space />

                {props.onCancel && (
                  <Button isWithBorder onClick={cancel}>
                    {t("utils_cancel")}
                  </Button>
                )}

                {props.footer}

                {props.onNext && (
                  <Tooltip text={props.nextDisabledTooltip} position={props.nextDisabledTooltipPosition ?? "top"}>
                    <Button
                      className="primary"
                      isLoading={props.status === STATUS_SAVING}
                      onClick={next}
                      disabled={props.nextDisabled}>
                      {props.onNextCta ? props.onNextCta : t("utils_next")}
                    </Button>
                  </Tooltip>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <AnimatePresence onExitComplete={handleExitComplete}>
      {!isClosing && (
        <motion.div
          key="modal-backdrop"
          ref={containerRef}
          className="modal-container flex"
          style={{ zIndex: props.zIndex }}
          onClick={() => clickOutside()}
          initial={isFading ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={isFading ? { opacity: 0 } : undefined}
          transition={{ duration: 0.18 }}>
          <motion.div
            key="modal-box"
            className={`${isFullScreen ? "modal-fullscreen" : "modal"} ${props.className || ""}`}
            onClick={(e) => {
              e.stopPropagation()
            }}
            initial={isFading ? { opacity: 0, scale: 0.96, y: 8 } : false}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={isFading ? { opacity: 0, scale: 0.96, y: 8 } : undefined}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={getStyle()}>
            <div className="flex" style={props.title ? { minHeight: 52 } : undefined}>
              {props.title && <h1>{props.title}</h1>}

              <Space />

              {props.onSearch && (
                <div style={{ width: "300px", marginLeft: "80px" }}>
                  <SearchBar
                    onSearch={search}
                    status={isSearching ? STATUS_SEARCHING : STATUS_LOADED}
                    onSearchStart={() => setIsSearching(true)}
                  />
                </div>
              )}
            </div>

            <div
              className={`${isFullScreen ? "modal-content-fullscreen" : "modal-content"} ${props.contentClassName || ""}`}
              style={{ overflowY: props.disableOverflow ? "initial" : "auto", maxHeight: props.contentHeight ?? undefined }}>
              {props.children}
            </div>

            <div className="relative flex">
              {props.footerStart}

              {props.onDelete && (
                <Button isWithBorder={isFullScreen} onClick={deleteItem}>
                  {t("utils_delete")}
                </Button>
              )}

              {props.footerLeft}

              <Space />

              {props.isCloseButtonVisible && (
                <Button isWithBorder={isFullScreen} onClick={close}>
                  {t("utils_close")}
                </Button>
              )}

              {props.onCancel && (
                <Button isWithBorder={isFullScreen} onClick={cancel}>
                  {t("utils_cancel")}
                </Button>
              )}

              {props.onNext && (
                <Tooltip text={props.nextDisabledTooltip} position={props.nextDisabledTooltipPosition ?? "top"}>
                  <Button
                    className="primary"
                    isLoading={props.status === STATUS_SAVING}
                    onClick={next}
                    disabled={props.nextDisabled}>
                    {props.onNextCta ? props.onNextCta : t("utils_next")}
                  </Button>
                </Tooltip>
              )}

              {props.footer}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default withTranslation()(Modal)
